
import {debounce} from "vue-debounce";
import { v4 as uuid } from 'uuid';
import XLSX from "xlsx";
import {Form} from "@/api/types/Form";
import {Question} from "@/api/types/Question";
import {Reply} from "@/api/types/Reply";
import {Answer} from "@/api/types/Answer";
import {Tenant} from "@/api/types/Tenant";
import * as formAPI from "@/api/forms";
import * as tenantAPI from "@/api/tenants";
import * as replyAPI from "@/api/replies";
import * as filesAPI from "@/api/files";
import FormBuilder from "@/components/form/FormBuilder.vue";
import FormPreview from "@/components/form/FormPreview.vue";
import FormSettings from "@/components/form/FormSettings.vue";
import Profile from "@/shared/mixins/Profile";
import mixins from "vue-typed-mixins";
import DeleteFormDialog from "@/components/form/DeleteFormDialog.vue";
import {ScoreRule} from "@/api/types/ScoreRule";
import DownloadJson from "@/components/form/DownloadJSON.vue";

const MIXINS = [Profile];
const Vue = mixins(...MIXINS);

export default Vue.extend({
  name: "Forms",
  components: {
    DownloadJson,
    DeleteFormDialog,
    FormSettings,
    FormPreview,
    FormBuilder,
  },
  data() {
    return {
      tab: 1,
      formError: false,
      formErrorMessage: '',
      filter: '',
      form: {
        title: "",
        description: "",
        photoUrl: "",
        questions: [
          {
            title: "",
            type: "text-field",
            required: false,
            options: [""],
            linearScale: {
              minValue: "0",
              maxValue: "1",
              minLabel: "",
              maxLabel: "",
            },
            position: 0,
            image: {
              file: null,
              deleting: false,
              deleteUrl: null
            }
          },
        ] as Partial<Question>[],
        scoreRules: [] as Partial<ScoreRule>[],
        scoreRulesStatus: false,
        tenantIds: [] as string[],
      } as Partial<Form>,
      forms: [] as Form[],
      dialog: false as boolean,
      excelDialog: false as boolean,
      limit: 50 as number,
      skip: 0 as number,
      total: 0 as number,
      loading: true as boolean,
      loadingEdit: false as boolean,
      canScroll: false as boolean,
      formId: -1 as number,
      editing: false as boolean,
      deleting: false as boolean,
      reply: {
        answers: [{value: []}] as Partial<Answer>[],
      } as Partial<Reply>,
      tenantsOptions: [] as Tenant[],
      tenants: [] as string[],
      selectedTenants: [] as Tenant[],
      selectedDownloadTenant: null as Tenant | null,
      copyFormLink: false as boolean,
      messageCopy: "" as string,
      deleteDialog: false as boolean,
      currentFormTitle: "" as string,
      currentFormId: -1 as number,
      excelFormId: 0 as number,
      excelFormTitle: '' as string,
      downloadingExcel: false as boolean,
      selectedForm: {} as unknown,
      jsonFile: null as unknown,
    };
  },
  async created() {
    await this.getTenants();
    this.getForms();
  },
  methods: {
    askDeleteForm(form: Form) {
      this.currentFormTitle = form.title;
      this.currentFormId = form.id;
      this.deleteDialog = true;
    },
    initForm(): void {
      this.form = {
        title: "",
        description: "",
        photoUrl:
            "https://www.ctvnews.ca/polopoly_fs/1.1967522.1584700888!/httpImage/image.jpg_gen/derivatives/landscape_1020/image.jpg",
        questions: [
          {
            keyId: uuid(),
            title: this.$t('forms.defaultData.id.text'),
            type: "text-field",
            required: true,
            options: [""],
            linearScale: {
              minValue: "0",
              maxValue: "1",
              minLabel: "",
              maxLabel: "",
            },
            image: {
              file: null,
              deleting: false,
              deleteUrl: null
            }
          },
          {
            keyId: uuid(),
            title: this.$t('forms.defaultData.name.text'),
            type: "text-field",
            required: true,
            options: [""],
            linearScale: {
              minValue: "0",
              maxValue: "1",
              minLabel: "",
              maxLabel: "",
            },
            image: {
              file: null,
              deleting: false,
              deleteUrl: null
            }
          },
        ] as Partial<Question>[],
        scoreRules: [] as Partial<ScoreRule>[],
        tenantIds: this.tenantsOptions.map((t) => t.id),
      } as Partial<Form>;

      this.reply.answers = [
        {value: []},
        {value: []},
      ]
      const formF: any = this.$refs.formBuilder;
      if (formF) {
        formF.resetValidation();
      }
    },
    getColFlex(): number {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return 12;
        case "sm":
          return 6;
        case "md":
          return 4;
        case "lg":
          return 3;
        case "xl":
          return 2;
        default:
          return 12;
      }
    },
    getRandomFormImage(index: number): string {
      let images = [
        "https://www.ctvnews.ca/polopoly_fs/1.1967522.1584700888!/httpImage/image.jpg_gen/derivatives/landscape_1020/image.jpg",
        "https://dlslab.com/wp-content/uploads/2020/12/covid-19-testing-centers-848x477.jpg",
        "https://www.tollgateclinic.com/wp-content/uploads/2013/04/tollgate14.jpg",
        "https://res.cloudinary.com/imap2/image/upload/c_fill,g_auto,h_630,q_auto:eco,w_1200/v1508233134/industries/industry-healthcare.jpg",
      ];
      // const random = Math.floor(Math.random() * images.length);

      return images[index % images.length];
    },
    async save(): Promise<void> {
      const form: any = this.$refs.formBuilder;

      // Ensure that the form has a tenant assigned.
      if(this.form && this.form.tenantIds?.length === 0) {
        this.formErrorMessage = this.$t('forms.form.validations.noTenantsSelected').toString();
        this.formError = true;
        return;
      }

      if (form && form.validate()) {
        this.loading = true;
        let position = 0;
        this.form.questions = this.form.questions?.map((elem) => {
          elem.position = position;
          position++;
          return elem;
        });
        if (this.form && this.form.scoreRules?.length) {
          this.form.scoreRules = this.form.scoreRules.map(elem => {
            if (elem && elem.value) {
              elem.value = +elem.value;
            }
            return elem;
          })
        }
        if (this.editing && this.form.id) {
          const imagesUrl = await this.uploadImages(this.form.id)
          imagesUrl.forEach((url, index) => {
            if (this.form.questions) {
              this.form.questions[index].imageUrl = url;
            }
          });
          // Edit form
          await formAPI
              .patch(this.form.id, this.form)
              .then(() => {
                // Fetch all forms again
                this.skip = 0;
                this.forms = [];
                this.getForms();
                this.closeDialog();
              })
              .catch((err) => {
                this.loading = false;
                throw err;
              });
        } else {
          const formCopy = Object.assign({}, this.form);
          // Create form
          await formAPI
              .create(this.form)
              .then(resp => {
                this.uploadImages(resp.id).then(imagesUrl => {
                  imagesUrl.forEach((url, index) => {
                    if (formCopy.questions) {
                      formCopy.questions[index].imageUrl = url;
                    }
                  })
                  return formAPI
                      .patch(resp.id, formCopy)
                      .then(() => {
                        // Fetch all forms again
                        this.skip = 0;
                        this.forms = [];
                        this.getForms();
                        this.closeDialog();
                      })
                })

              })
              .catch((err) => {
                this.loading = false;
                throw err;
              });
        }
      }
    },
    async uploadImages(formId: number) {
      if (!this.form.questions) {
        return [];
      }
      let urls = [];
      for (let question of this.form.questions) {
        const formData = new FormData();
        if (question && question.image && question.image.file == null && !question.image.deleting) {
          urls.push('');
        }
        else if (question && question.image && question.image.file == null && question.image.deleting) {
          if (question.image.deleteUrl) {
            await filesAPI.remove(question.image.deleteUrl);
          }
          urls.push('');
        }
        else if (question && question.image && question.image.file && question.imageUrl && question.imageUrl.split(':')[0] === 'blob') {
          formData.append("file", question.image.file);
          formData.append("formId", `${formId}`);
          const response = await filesAPI.create(formData)
          urls.push(response.url);
        }
        else {
          urls.push(question.imageUrl);
        }
      }
      return urls;
    },
    getForms(): void {
      this.loading = true;
      // Fetch four rows at a time
      this.limit = 4 * (12 / this.getColFlex());
      formAPI.list(this.limit, this.skip, this.filter, this.tenants).then((resp) => {
        if (resp.data && resp.data.length > 0) {
          resp.data.forEach(elem => {
            if(!this.forms.find(f => f.id === elem.id)) this.forms.push(elem)
          });
          this.total = resp.total;
          this.skip += resp.data.length;
        }
        this.loading = false;
      });
    },
    infiniteScrolling() {
      // Only if we have not fetched all data
      if (this.total !== 0 && this.skip !== this.total) {
        setTimeout(() => {
          this.canScroll = true;
        }, 1000);
        if (!this.loading && this.forms.length > 0 && this.canScroll) {
          this.canScroll = false;
          this.getForms();
        }
      }
    },
    deleteForm(id: number) {
      this.deleting = true;
      formAPI
          .patch(id, {active: false})
          .then((resp) => {
            if (!resp.active) {
              this.forms = this.forms.filter((elem) => elem.id !== resp.id);
            }
            this.deleting = false;
            this.deleteDialog = false;
          })
          .catch((err) => {
            this.deleteDialog = false;
            throw err;
          });
    },
    edit(form: Form): void {
      // Get form answers from API
      this.loadingEdit = true;
      formAPI
          .fetch(form.id)
          .then(async (completeForm) => {
            this.form = Object.assign({}, completeForm);
            this.reply.answers = [];
            if (this.form.questions) {
              for (let question of this.form.questions) {
                question.keyId = uuid();
                if (question.imageUrl) {
                  const myFile = await this.createFile(question.imageUrl)
                  question.image = {
                    file: myFile,
                    deleting: false,
                    deleteUrl: null
                  }
                }
                else {
                  question.image = {
                    file: null,
                    deleting: false,
                    deleteUrl: null
                  }
                }
                this.reply.answers?.push({
                  value: [],
                  type: question.type,
                  questionId: question.id,
                })
              }
            }

            if (this.form && this.form.scoreRules) {
              this.form.scoreRulesStatus = this.form?.scoreRules.length > 0;
            }
            this.editing = true;
            this.loadingEdit = false;
            this.dialog = true;
            this.$forceUpdate();
          })
          .catch((err) => {
            this.loadingEdit = false;
            throw err;
          });
    },
    async createFile(url: string){
      let response = await fetch(url);
      let data = await response.blob();
      const fileName = url.split("/")[url.split("/").length - 1];
      const fileType = url.split(".")[url.split(".").length - 1];
      let metadata = {
        type: `image/${fileType}`
      };
      return  new File([data], fileName, metadata);

    },
    closeDialog(): void {
      this.editing = false;
      this.initForm();
      this.getTenants();
      this.dialog = false;
    },
    cleanForm(): void {
      this.reply.answers?.map((elem) => {
        elem.value = [];
        return elem;
      });
    },
    addReply() {
      this.reply.answers?.push({value: [], type: "text-field"});
    },
    async getTenants() {
      if(this.profileRole == "admin") {
        const tenants = await tenantAPI.listAll();
        this.tenantsOptions = tenants.data;
        this.tenants = tenants.data.map((elem) => elem.id);
      } else {
        const tenants = await tenantAPI.list();
        this.tenantsOptions = tenants;
        this.tenants = tenants.map((elem) => elem.id);
      }
    },
    changedTenants() {
      debounce(() => {
        this.forms = [];
        this.skip = 0;
        this.getForms();
      }, 400)();
    },
    clearReply() {
      this.reply.answers?.forEach((val, index) => {
        if (this.reply.answers && this.reply.answers[index].value) {
          this.reply.answers[index].value = [];
        }
      });
    },
    openNewForm() {
      this.initForm();
      this.form.tenantIds = [];
      this.dialog = true;
    },
    answerForm(id: number) {
      this.$router.push(`form-reply/${id}`).catch((err) => {
        console.log(err);
      });
    },
    shareLink(id: number) {
      this.$copyText(window.location.origin + `/form-reply/${id}`).then(
          () => {
            this.messageCopy = this.$t("forms.copyLink").toString();
            this.copyFormLink = true;
          },
          (e) => {
            console.log(e);
          }
      );
    },
    workbook2blob(workbook: any) {
      // Generar elementos de configuración para excel
      var wopts: any = {
        // Tipo de archivo a generar
        bookType: "xlsx",
        // // Ya sea para generar Shared String Table, la explicación oficial es que si está encendida, la velocidad de generación disminuirá, pero hay una mejor compatibilidad en dispositivos IOS de versiones inferiores
        bookSST: false,
        type: "binary",
      };
      let wbout = XLSX.write(workbook, wopts);

      // Convertir cadena en ArrayBuffer
      function s2ab(s: any) {
        var buf = new ArrayBuffer(s.length);
        var view = new Uint8Array(buf);
        for (var i = 0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xff;
        return buf;
      }

      let buf = s2ab(wbout);
      let blob = new Blob([buf], {
        type: "application/octet-stream",
      });
      return blob;
    },
    openDownloadDialog(blob: any, fileName: string) {
      if (typeof blob === "object" && blob instanceof Blob) {
        blob = URL.createObjectURL(blob); // Crear dirección de blob
      }
      let aLink = document.createElement("a");
      aLink.href = blob;
      aLink.download = fileName || "";
      let event;
      if (window.MouseEvent) event = new MouseEvent("click");
      else {
        event = document.createEvent("MouseEvents");
        event.initMouseEvent(
            "click",
            true,
            false,
            window,
            0,
            0,
            0,
            0,
            0,
            false,
            false,
            false,
            false,
            0,
            null
        );
      }
      aLink.dispatchEvent(event);
    },
    downloadExcel() {
      this.downloadingExcel = true;
      let selectedTenantId = this.selectedDownloadTenant?.id || undefined;
      replyAPI
          .list(undefined, undefined, this.excelFormId, undefined, undefined, selectedTenantId)
          .then((resp: any) => {
            const answers: any = resp.data.map((row: any) => {
              let obj: any = {};
              row.answers.forEach((ans: any) => {
                obj[ans.question] = ans.answer.join(',');
              });
              return obj;
            });
            let sheet1 = XLSX.utils.json_to_sheet(answers);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(
                wb,
                sheet1,
                this.$t("forms.headers.answers").toString()
            );
            const workbookBlob = this.workbook2blob(wb);
            this.openDownloadDialog(
                workbookBlob,
                `${this.excelFormTitle}_${new Date().toISOString()}.xlsx`
            );

            this.excelFormId = 0;
            this.excelFormTitle = '';
            this.selectedDownloadTenant = null;
            this.excelDialog = false;
            this.downloadingExcel = false;
          })
          .catch((err) => {
            console.log(err);
            this.downloadingExcel = false;
          });
    },
    exportExcel(formId: number, formTitle: string) {
      this.excelFormId = formId;
      this.excelFormTitle = formTitle;
      this.selectedDownloadTenant = this.tenantsOptions.length === 1? this.tenantsOptions[0] : null;
      this.excelDialog = true;
    },
    goToReplies(formId: number, formUID: string) {
      this.$router.push(`form-replies-list/${formId}/${formUID}`).catch((err) => {
        console.log(err);
      });
    },
    hasPermission(operation: string, form: Form) {
      if (this.profileRole === "admin") {
        return true;
      }

      if (this.profileRole === "maintainer") {

        return form.createdBy === this.profileUserId || !["update", "delete"].includes(operation);
      }

      return false;
    },
    openDownloadJson(form: Form) {
      formAPI
          .fetch(form.id)
          .then(async (completeForm) => {
            this.selectedForm = Object.assign({}, completeForm);
            const dialog: any = this.$refs.downloadJSONDialog;
            if (dialog) {
              dialog.openDialog();
            }
          });
    },
    loadJson() {
      // eslint-disable-next-line
      const input: any = this.$refs['jsonFileInput'];
      if (input) {
        input.$refs.input.click();
      }
    },
    loadedJson() {
      (this.jsonFile as File).text().then(async (text) => {
        this.form = JSON.parse(text);
        this.reply.answers = [];
        delete this.form.id;
        if (this.form.questions) {
          for (let question of this.form.questions) {
            delete question.id;
            if (question.imageUrl) {
              const myFile = await this.createFile(question.imageUrl)
              question.image = {
                file: myFile,
                deleting: false,
                deleteUrl: null
              }
            }
            else {
              question.image = {
                file: null,
                deleting: false,
                deleteUrl: null
              }
            }
            this.reply.answers?.push({
              value: [],
              type: question.type,
              questionId: question.id,
            })
          }
        }

        if (this.form && this.form.scoreRules) {
          this.form.scoreRulesStatus = this.form?.scoreRules.length > 0;
        }
      })
    },
    toggleSelectAllTenants() {
      if(this.tenants.length === 0 || this.someTenantsSelected) {
        this.tenants = this.tenantsOptions.map((elem) => elem.id);
      } else {
        this.tenants = [];
      }
      this.changedTenants();
    },
    filterForms() {
      debounce(() => {
        this.forms = [];
        this.skip = 0;
        this.getForms();
      }, 400)();
    },
  },
  computed: {
    isMaintainer: {
      get() {
        return ["admin", "maintainer"].includes(this.profileRole);
      },
    },
    isAdmin: {
      get() {
        return ["admin"].includes(this.profileRole);
      },
    },
    allTenantsSelected(): boolean {
      return this.tenants.length === this.tenantsOptions.length;
    },
    someTenantsSelected(): boolean {
      return this.tenants.length > 0 && this.tenants.length !== this.tenantsOptions.length;
    },
  },
});
